import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  FlatList,
  Modal,
  PanResponder,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import { format } from "date-fns";

import { useAuth } from "../../../context/AuthContext";
import ApprovalConfirmModal from "../../shared/utils/ApprovalConfirmModal";
import {
  AdminService,
  DivineCareFilterDto,
  NotifyAllDto,
} from "../../services/AdminService";
import { Flash } from "../../shared/utils/Flash";

type Props = {
  visible: boolean;
  selectedDevotee?: {
    userId?: string;
    name?: string | null;
  };
};

type DivineCareAlert = {
  id: string;
  type: string;
  description: string;
};

type DivineCareItem = {
  id: string;
  devoteeId: string;
  name: string;
  mobileNumber: string;
  alerts: DivineCareAlert[];
  alertCount: number;
};

type DivineCareSummary = {
  totalAlerts: number;
  totalCases: number;
};

type BulkActionType = "notifyAll" | "archiveAll";

const DEFAULT_PAGE_SIZE = 1000;

const toDisplayText = (value: unknown, fallback = "") => {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
};

const toSentenceCase = (value: string) =>
  value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getArrayPayload = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.records)) return data.records;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.divineCare)) return data.divineCare;
  if (Array.isArray(data?.devotees)) return data.devotees;
  if (Array.isArray(data?.alerts)) return data.alerts;
  return [];
};

const normalizeAlert = (alert: any, fallbackKey: string, index: number): DivineCareAlert => ({
  id: toDisplayText(alert?.id ?? alert?.alertId ?? `${fallbackKey}-alert-${index}`),
  type: toSentenceCase(
    toDisplayText(alert?.type ?? alert?.category ?? alert?.alertType ?? "General")
  ),
  description: toDisplayText(
    alert?.description ?? alert?.message ?? alert?.details ?? alert?.reason,
    "Needs your divine attention."
  ),
});

const normalizeItem = (item: any, index: number): DivineCareItem => {
  const rawAlerts = Array.isArray(item?.alerts)
    ? item.alerts
    : Array.isArray(item?.divineCareAlerts)
      ? item.divineCareAlerts
      : Array.isArray(item?.issues)
        ? item.issues
        : Array.isArray(item?.items)
          ? item.items
          : [];

  const devoteeId = toDisplayText(
    item?.devoteeId ??
    item?.userId ??
    item?.id ??
    item?.mobileNumber ??
    item?.mobileNo ??
    `divine-care-${index}`
  );

  const alerts =
    rawAlerts.length > 0
      ? rawAlerts.map((alert: any, alertIndex: number) => normalizeAlert(alert, devoteeId, alertIndex))
      : [
        normalizeAlert(
          {
            type: item?.type ?? item?.category ?? item?.alertType ?? "General",
            description: item?.description ?? item?.message ?? item?.details ?? item?.reason,
          },
          devoteeId,
          0
        ),
      ];

  return {
    id: devoteeId,
    devoteeId,
    name: toDisplayText(item?.name ?? item?.fullName ?? item?.devoteeName, "Unknown Devotee"),
    mobileNumber: toDisplayText(item?.mobileNumber ?? item?.mobileNo ?? item?.phoneNumber),
    alerts,
    alertCount: Number(item?.alertCount ?? item?.totalAlerts ?? alerts.length) || alerts.length,
  };
};

type SwipeableDevoteeCardProps = {
  children: React.ReactNode;
  onArchivePress: () => void;
  onCardPress?: () => void;
  disabled?: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

const ARCHIVE_ACTION_WIDTH = 104;
const SWIPE_REVEAL_THRESHOLD = 52;

function SwipeableDevoteeCard({
  children,
  onArchivePress,
  onCardPress,
  disabled = false,
  isOpen,
  onOpen,
  onClose,
}: SwipeableDevoteeCardProps) {
  const translateX = useRef(new Animated.Value(0)).current;
  const [isArchiveVisible, setIsArchiveVisible] = useState(false);

  const animateTo = (toValue: number, shouldShowArchive: boolean) => {
    setIsArchiveVisible(shouldShowArchive);
    Animated.spring(translateX, {
      toValue,
      useNativeDriver: true,
      bounciness: 6,
    }).start();
  };

  useEffect(() => {
    if (isOpen) {
      animateTo(-ARCHIVE_ACTION_WIDTH, true);
    } else {
      animateTo(0, false);
    }
  }, [isOpen]);


  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, gestureState) => {
        if (disabled) return false;
        return Math.abs(gestureState.dx) > 12 && Math.abs(gestureState.dy) < 10;
      },

      onPanResponderMove: (_, gestureState) => {
        if (disabled) return;
        const clampedTranslate = Math.max(-ARCHIVE_ACTION_WIDTH, Math.min(0, gestureState.dx));
        translateX.setValue(clampedTranslate);
        setIsArchiveVisible(clampedTranslate <= -SWIPE_REVEAL_THRESHOLD);
      },

      onPanResponderRelease: (_, gestureState) => {
        if (disabled) return;

        if (gestureState.dx <= -SWIPE_REVEAL_THRESHOLD) {
          onOpen();
          return;
        }

        onClose();
      },

      onPanResponderTerminate: onClose,
    })
  ).current;

  return (
    <View style={styles.swipeableWrap}>
      <View pointerEvents="box-none" style={styles.archiveActionWrap}>
        {isArchiveVisible ? (
          <Pressable
            onPress={onArchivePress}
            disabled={disabled}
            style={({ pressed }) => [
              styles.archiveActionButton,
              disabled ? styles.archiveActionDisabled : null,
              pressed && !disabled ? styles.archiveActionPressed : null,
            ]}
          >
            <Icon name="archive-outline" size={16} color="#FFFFFF" />
            <Text style={styles.archiveActionText}>Archive</Text>
          </Pressable>
        ) : null}
      </View>

      <Animated.View
        {...panResponder.panHandlers}
        style={{
          transform: [{ translateX }],
        }}
      >
        <Pressable onPress={onCardPress} disabled={disabled}>
          {children}
        </Pressable>
      </Animated.View>
    </View>
  );
}

export function DivineCareScreen({ visible, selectedDevotee }: Props) {
  const { user } = useAuth();
  const isDevotee = user?.userType === "Devotee";
  const isGuru = user?.userType === "Guru";
  const [items, setItems] = useState<DivineCareItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [draftName, setDraftName] = useState(selectedDevotee?.name?.trim() || "");
  const [draftFromDate, setDraftFromDate] = useState("");
  const [draftToDate, setDraftToDate] = useState("");
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [notifyingId, setNotifyingId] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<BulkActionType | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [openSwipeId, setOpenSwipeId] = useState<string | null>(null);

  const [appliedFilters, setAppliedFilters] = useState<DivineCareFilterDto>({
    Name: isDevotee ? null : selectedDevotee?.name?.trim() || null,
    FromDate: null,
    ToDate: null,
    PageSize: DEFAULT_PAGE_SIZE,
    Page: 1,
  });

  const [summary, setSummary] = useState<DivineCareSummary>({
    totalAlerts: 0,
    totalCases: 0,
  });
  const hasDivineCareItems = items.length > 0;

  const loadDivineCare = useCallback(
    async (nextFilters: DivineCareFilterDto) => {
      if (!visible) return;

      setLoading(true);
      try {
        const response = await AdminService.getDivineCare({
          ...nextFilters,
          Name: isDevotee ? null : nextFilters.Name ?? null,
        });

        if (Number(response?.status) !== 200) {
          Flash.error(response?.message?.trim() || "Unable to load divine care right now.");
          return;
        }

        const payload = response?.data;
        const normalizedItems = getArrayPayload(payload).map(normalizeItem);

        const totalAlerts =
          Number(payload?.totalAlerts ?? payload?.alertCount ?? payload?.totalCount) ||
          normalizedItems.reduce((sum, item) => sum + item.alertCount, 0);

        const totalCases =
          Number(payload?.totalIssues ?? payload?.caseCount ?? payload?.recordCount) ||
          normalizedItems.length;
        setItems(normalizedItems);
        setExpandedIds(
          normalizedItems.reduce((acc, current) => {
            acc[current.devoteeId] = false;
            return acc;
          }, {} as Record<string, boolean>)
        );

        setSummary({
          totalAlerts,
          totalCases,
        });
      } catch (error: any) {
        Flash.error(error?.message?.trim() || "Unable to load divine care right now.");
      } finally {
        setLoading(false);
        setOpenSwipeId(null);
      }
    },
    [isDevotee, visible]
  );

  useEffect(() => {
    if (!visible) return;
    loadDivineCare(appliedFilters);
  }, [visible, appliedFilters, loadDivineCare]);

  useEffect(() => {
    if (isDevotee) {
      if (draftName !== "") {
        setDraftName("");
      }

      setAppliedFilters((current) =>
        current.Name !== null
          ? {
            ...current,
            Name: null,
          }
          : current
      );
      return;
    }

    const selectedName = selectedDevotee?.name?.trim() || "";
    if (!selectedName) {
      return;
    }

    setDraftName(selectedName);
    setAppliedFilters((current) => ({
      ...current,
      Name: selectedName,
      Page: 1,
    }));
  }, [draftName, isDevotee, selectedDevotee?.name]);

  const openDatePicker = (type: "from" | "to") => {
    const currentValue = type === "from" ? draftFromDate : draftToDate;
    DateTimePickerAndroid.open({
      value: currentValue ? new Date(currentValue) : new Date(),
      mode: "date",
      onChange: (_, date) => {
        if (!date) return;
        const formatted = format(date, "yyyy-MM-dd");
        if (type === "from") setDraftFromDate(formatted);
        else setDraftToDate(formatted);
      },
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      Name: isDevotee ? null : draftName.trim() || null,
      FromDate: draftFromDate || null,
      ToDate: draftToDate || null,
      PageSize: DEFAULT_PAGE_SIZE,
      Page: 1,
    });
    setFilterVisible(false);
  };

  const handleClearFilters = () => {
    setDraftName(isDevotee ? "" : selectedDevotee?.name?.trim() || "");
    setDraftFromDate("");
    setDraftToDate("");
    setAppliedFilters({
      Name: isDevotee ? null : selectedDevotee?.name?.trim() || null,
      FromDate: null,
      ToDate: null,
      PageSize: DEFAULT_PAGE_SIZE,
      Page: 1,
    });
    setFilterVisible(false);
  };

  const toggleExpanded = (devoteeId: string) => {
    setExpandedIds((prev) => ({
      ...prev,
      [devoteeId]: !prev[devoteeId],
    }));
  };

  const handleArchive = async (devoteeId: string) => {
    if (!devoteeId) {
      Flash.error("Devotee id not found.");
      await loadDivineCare(appliedFilters);
      return;
    }
    const payload = {
      DevoteeId: devoteeId,
    };

    setArchivingId(devoteeId);
    try {
      const response = await AdminService.archiveDivineCare(payload);

      if (Number(response?.status) !== 200) {
        Flash.error(response?.message?.trim() || "Unable to archive devotee alerts right now.");
        return;
      }

      Flash.success("Success", "Devotee alerts archived successfully.");
    } catch (error: any) {
      Flash.error(error?.message?.trim() || "Unable to archive devotee alerts right now.");
    } finally {
      setArchivingId(null);
      setOpenSwipeId(null);
      await loadDivineCare(appliedFilters);
    }
  };

  const handleNotify = async (devoteeId: string) => {
    
    if (!devoteeId) {
      Flash.error("Devotee id not found.");
      return;
    }

    setNotifyingId(devoteeId);
    try {
      const payload = {
        DevoteeId: devoteeId,
      };

      const response = await AdminService.notifyDivineCareUser(payload);

      if (Number(response?.status) !== 200) {
        Flash.error(response?.message?.trim() || "Failed to send notification");
        return;
      }

      Flash.success("Success", "Notification sent successfully");
    } catch (error: any) {
      Flash.error(error?.message?.trim() || "Failed to send notification.");
    } finally {
      setNotifyingId(null);
    }
  };

  const openBulkActionConfirm = (type: BulkActionType) => {
    setBulkAction(type);
  };

  const closeBulkActionConfirm = () => {
    if (bulkActionLoading) {
      return;
    }

    setBulkAction(null);
  };

  const handleBulkAction = async () => {
    if (!bulkAction) {
      return;
    }

    const payload: NotifyAllDto = {
      Filter: appliedFilters,
    };

    setBulkActionLoading(true);
    try {
      const response =
        bulkAction === "notifyAll"
          ? await AdminService.notifyAllDivineCareUsers(payload)
          : await AdminService.archiveAllDivineCare(payload);

      if (Number(response?.status) !== 200) {
        Flash.error(
          response?.message?.trim() ||
          (bulkAction === "notifyAll"
            ? "Unable to notify all devotees right now."
            : "Unable to archive all alerts right now.")
        );
        return;
      }

      Flash.success(
        "Success",
        bulkAction === "notifyAll"
          ? "All notifications sent successfully."
          : "All alerts archived successfully."
      );

      if (bulkAction === "archiveAll") {
        await loadDivineCare(appliedFilters);
      }

      setBulkAction(null);
    } catch (error: any) {
      Flash.error(
        error?.message?.trim() ||
        (bulkAction === "notifyAll"
          ? "Unable to notify all devotees right now."
          : "Unable to archive all alerts right now.")
      );
    } finally {
      setBulkActionLoading(false);
    }
  };

  const renderHeader = () => (
    <View style={styles.headerWrap}>
      <View style={styles.heroCard}>
        <View style={styles.heroIconWrap}>
          <Icon name="heart-outline" size={22} color="#C02655" />
        </View>
        <View style={styles.heroCopy}>
          <Text style={styles.heroTitle}>Divine Care Alerts</Text>
          <Text style={styles.heroSubtitle}>Alerts needs your attention.</Text>
        </View>
        <Pressable style={styles.filterIconButton} onPress={() => setFilterVisible(true)}>
          <Icon name="filter-variant" size={20} color="#D9466F" />
        </Pressable>
      </View>

      <View style={styles.summaryRow}>
        <View style={styles.summaryCardWarm}>
          <View style={styles.summaryHeader}>
            <Icon name="alert-outline" size={16} color="#DC2626" />
            <Text style={styles.summaryLabel}>Total Alerts</Text>
          </View>
          <Text style={styles.summaryValueWarm}>{summary.totalAlerts}</Text>
        </View>

        <View style={styles.summaryCardSoft}>
          <View style={styles.summaryHeader}>
            <Icon name="bell-outline" size={16} color="#C2410C" />
            <Text style={styles.summaryLabel}>Issues</Text>
          </View>
          <Text style={styles.summaryValueSoft}>{summary.totalCases}</Text>
        </View>
      </View>

      {isGuru && hasDivineCareItems ? (
        <View style={styles.topActionsRow}>
          <TouchableOpacity
            style={[styles.topActionButton, styles.topActionArchive]}
            onPress={() => openBulkActionConfirm("archiveAll")}
            disabled={bulkActionLoading}
          >
            <Icon name="archive-outline" size={16} color="#FFFFFF" />
            <Text style={styles.topActionText}>Archive All</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.topActionButton, styles.topActionNotify]}
            onPress={() => openBulkActionConfirm("notifyAll")}
            disabled={bulkActionLoading}
          >
            <Icon name="bell-ring-outline" size={16} color="#FFFFFF" />
            <Text style={styles.topActionText}>Notify All</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );

  const renderItem = ({ item }: { item: DivineCareItem }) => {
    const isExpanded = !!expandedIds[item.devoteeId];
    const isArchiving = archivingId === item.devoteeId;
    const isNotifying = notifyingId === item.devoteeId;

    return (
      <SwipeableDevoteeCard
        onCardPress={() => toggleExpanded(item.devoteeId)}
        onArchivePress={() => handleArchive(item.devoteeId)}
        disabled={isArchiving || isNotifying || bulkActionLoading}
        isOpen={openSwipeId === item.devoteeId}
        onOpen={() => setOpenSwipeId(item.devoteeId)}
        onClose={() =>
          setOpenSwipeId((current) =>
            current === item.devoteeId ? null : current
          )
        }
      >
        <View style={[styles.card, isArchiving ? styles.cardDisabled : null]}>
          <View style={styles.cardHeader}>
            <View style={styles.avatarBadge}>
              <Text style={styles.avatarBadgeText}>{item.name.slice(0, 1).toUpperCase()}</Text>
            </View>

            <View style={styles.userCopy}>
              <Text style={styles.userName} numberOfLines={1}>
                {item.name}
              </Text>
              <Text style={styles.userMobile} numberOfLines={1}>
                {item.mobileNumber}
              </Text>
            </View>

            <View style={styles.headerActions}>
              {isGuru ? (
                <TouchableOpacity
                  style={[styles.actionPill, styles.notifyButton, isNotifying ? styles.notifyButtonDisabled : null]}
                  onPress={() => handleNotify(item.devoteeId)}
                  disabled={isNotifying || isArchiving || bulkActionLoading}
                >
                  <Icon name="bell-ring-outline" size={13} color="#FFFFFF" />
                  <Text style={styles.notifyButtonText}>
                    {isNotifying ? "..." : "Notify"}
                  </Text>
                </TouchableOpacity>
              ) : null}

              <View style={[styles.actionPill, styles.alertCountPill]}>
                <Text style={styles.alertCountText}>
                  {`${item.alertCount} Alert${item.alertCount === 1 ? "" : "s"}`}
                </Text>
              </View>

              <Pressable
                onPress={() => toggleExpanded(item.devoteeId)}
                style={styles.expandButton}
              >
                <Icon
                  name={isExpanded ? "chevron-up" : "chevron-down"}
                  size={22}
                  color="#BE123C"
                />
              </Pressable>
            </View>
          </View>

          <Text style={styles.swipeHint}>Swipe left a little to reveal archive</Text>

          {isExpanded ? (
            <>
              {item.alerts.map((alert) => (
                <View key={alert.id} style={styles.alertRow}>
                  <View style={styles.alertTypeBadge}>
                    <Text style={styles.alertTypeText}>{alert.type}</Text>
                  </View>
                  <Text style={styles.alertDescription}>{alert.description}</Text>
                </View>
              ))}
            </>
          ) : null}
        </View>
      </SwipeableDevoteeCard>
    );
  };

  return (
    <View style={styles.container}>
      {renderHeader()}

      {loading && items.length === 0 ? (
        <View style={styles.loaderWrap}>
          <ActivityIndicator size="large" color="#E11D48" />
        </View>
      ) : (
        <FlatList
          data={items}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <Icon name="heart-flash" size={24} color="#FB7185" />
              <Text style={styles.emptyTitle}>No divine care alerts found.</Text>
              <Text style={styles.emptySubtitle}>Try another filter or date range.</Text>
            </View>
          }
          ListFooterComponent={<View style={styles.footerSpace} />}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator
        />
      )}

      <Modal
        visible={filterVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setFilterVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalSheet}>
            <View style={styles.modalHeader}>
              <View style={styles.modalTitleWrap}>
                <Icon name="filter-variant" size={20} color="#EA580C" />
                <Text style={styles.modalTitle}>Filter Alerts</Text>
              </View>
              <Pressable onPress={() => setFilterVisible(false)}>
                <Icon name="close" size={20} color="#94A3B8" />
              </Pressable>
            </View>

            {!isDevotee ? (
              <>
                <Text style={styles.fieldLabel}>Devotee Name</Text>
                <TextInput
                  value={draftName}
                  onChangeText={setDraftName}
                  placeholder="Search by name..."
                  placeholderTextColor="#94A3B8"
                  style={styles.input}
                />
              </>
            ) : null}

            <View style={styles.datePickerRow}>
              <View style={styles.datePickerWrap}>
                <Text style={styles.fieldLabel}>From Date</Text>
                <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker("from")}>
                  <Text style={draftFromDate ? styles.dateValue : styles.datePlaceholder}>
                    {draftFromDate || "dd-mm-yyyy"}
                  </Text>
                  <Icon name="calendar-month-outline" size={18} color="#E11D48" />
                </TouchableOpacity>
              </View>

              <View style={styles.datePickerWrap}>
                <Text style={styles.fieldLabel}>To Date</Text>
                <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker("to")}>
                  <Text style={draftToDate ? styles.dateValue : styles.datePlaceholder}>
                    {draftToDate || "dd-mm-yyyy"}
                  </Text>
                  <Icon name="calendar-month-outline" size={18} color="#E11D48" />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.clearButton} onPress={handleClearFilters}>
                <Text style={styles.clearButtonText}>Clear All</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.applyButton} onPress={handleApplyFilters}>
                <Text style={styles.applyButtonText}>Apply Filters</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <ApprovalConfirmModal
        visible={!!bulkAction}
        title={bulkAction === "notifyAll" ? "Notify All Devotees?" : "Archive All Alerts?"}
        message={
          bulkAction === "notifyAll"
            ? "Hare Krishna. Do you want to send divine care notifications to all devotees in the current filtered list?"
            : "Hare Krishna. Do you want to archive all divine care alerts in the current filtered list?"
        }
        confirmLabel={bulkAction === "notifyAll" ? "Notify All" : "Archive All"}
        variant={bulkAction === "notifyAll" ? "approve" : "warning"}
        loading={bulkActionLoading}
        onClose={closeBulkActionConfirm}
        onConfirm={handleBulkAction}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  listContent: { paddingHorizontal: 14, paddingBottom: 28, paddingTop: 2 },
  headerWrap: { paddingTop: 14, paddingHorizontal: 14, gap: 12 },

  heroCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: "rgba(255,255,255,0.96)",
    borderWidth: 1,
    borderColor: "rgba(251,113,133,0.18)",
  },
  heroIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(244,114,182,0.12)",
  },
  heroCopy: { flex: 1, gap: 2 },
  heroTitle: { fontSize: 15, fontWeight: "800", color: "#3F1D2E" },
  heroSubtitle: { fontSize: 12, fontWeight: "600", color: "rgba(63,29,46,0.62)" },
  filterIconButton: {
    width: 36,
    height: 36,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(251,113,133,0.08)",
  },

  summaryRow: { flexDirection: "row", gap: 12 },
  summaryHeader: { flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 6 },
  summaryCardWarm: {
    flex: 1,
    borderRadius: 18,
    padding: 14,
    backgroundColor: "rgba(255,250,250,0.98)",
    borderWidth: 1,
    borderColor: "rgba(251,113,133,0.18)",
  },
  summaryCardSoft: {
    flex: 1,
    borderRadius: 18,
    padding: 14,
    backgroundColor: "rgba(255,250,245,0.98)",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.18)",
  },
  summaryLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: "rgba(71,85,105,0.85)",
    textTransform: "uppercase",
  },
  summaryValueWarm: { fontSize: 28, fontWeight: "900", color: "#DC2626" },
  summaryValueSoft: { fontSize: 28, fontWeight: "900", color: "#C2410C" },
  topActionsRow: {
    flexDirection: "row",
    gap: 10,
  },
  topActionButton: {
    flex: 1,
    minHeight: 42,
    borderRadius: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  topActionArchive: {
    backgroundColor: "#FB7185",
  },
  topActionNotify: {
    backgroundColor: "#64748B",
  },
  topActionText: {
    fontSize: 12,
    fontWeight: "800",
    color: "#FFFFFF",
  },

  loaderWrap: { flex: 1, alignItems: "center", justifyContent: "center", padding: 24 },

  card: {
    padding: 14,
    borderRadius: 20,
    backgroundColor: "rgba(255,255,255,0.97)",
    borderWidth: 1,
    borderColor: "rgba(251,113,133,0.15)",
  },
  cardDisabled: {
    opacity: 0.7,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 10,
  },
  avatarBadge: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#BE123C",
  },
  avatarBadgeText: { color: "#FFFFFF", fontSize: 16, fontWeight: "900" },
  userCopy: { flex: 1, gap: 2, minWidth: 0 },
  userName: { fontSize: 14, fontWeight: "800", color: "#2F1B22" },
  userMobile: { fontSize: 11, fontWeight: "600", color: "#64748B" },

  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    alignSelf: "flex-start",
  },
  actionPill: {
    minHeight: 28,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  alertCountPill: {
    backgroundColor: "#BE123C",
  },
  alertCountText: { color: "#FFFFFF", fontSize: 11, fontWeight: "800" },

  expandButton: {
    width: 25,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 4,
  },

  swipeHint: {
    fontSize: 10,
    fontWeight: "700",
    color: "#94A3B8",
    textAlign: "right",
    marginTop: 2,
    marginBottom: 4,
  },

  alertRow: {
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "rgba(255,248,248,0.92)",
    borderWidth: 1,
    borderColor: "rgba(251,113,133,0.12)",
    marginTop: 8,
  },
  alertTypeBadge: {
    alignSelf: "flex-start",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: "rgba(251,113,133,0.10)",
    marginBottom: 6,
  },
  alertTypeText: {
    fontSize: 10,
    fontWeight: "800",
    color: "#BE123C",
    textTransform: "uppercase",
  },
  alertDescription: {
    fontSize: 12,
    lineHeight: 18,
    color: "#4B5563",
    fontWeight: "600",
  },

  emptyState: {
    marginTop: 16,
    borderRadius: 20,
    padding: 22,
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.96)",
    borderWidth: 1,
    borderColor: "rgba(251,113,133,0.12)",
  },
  emptyTitle: { marginTop: 10, fontSize: 14, fontWeight: "800", color: "#3F1D2E" },
  emptySubtitle: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: "600",
    color: "#64748B",
    textAlign: "center",
  },
  footerSpace: { height: 20 },

  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(15,23,42,0.35)",
  },
  modalSheet: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 18,
    paddingTop: 18,
    paddingBottom: Platform.OS === "android" ? 22 : 34,
    gap: 12,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalTitleWrap: { flexDirection: "row", alignItems: "center", gap: 8 },
  modalTitle: { fontSize: 16, fontWeight: "800", color: "#1F2937" },

  fieldLabel: { fontSize: 12, fontWeight: "700", color: "#475569" },
  input: {
    height: 46,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(203,213,225,0.8)",
    paddingHorizontal: 12,
    color: "#0F172A",
    fontWeight: "600",
    backgroundColor: "#FFFFFF",
  },
  datePickerRow: { flexDirection: "row", gap: 12 },
  datePickerWrap: { flex: 1, gap: 6 },
  dateInput: {
    height: 46,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(203,213,225,0.8)",
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dateValue: { fontSize: 13, fontWeight: "700", color: "#0F172A" },
  datePlaceholder: { fontSize: 13, fontWeight: "600", color: "#94A3B8" },

  modalActions: { marginTop: 6, flexDirection: "row", gap: 12 },
  clearButton: {
    flex: 1,
    minHeight: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(203,213,225,0.9)",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  clearButtonText: { fontSize: 13, fontWeight: "800", color: "#475569" },
  applyButton: {
    flex: 1.4,
    minHeight: 48,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FB7185",
  },
  applyButtonText: { fontSize: 13, fontWeight: "800", color: "#FFFFFF" },
  swipeableWrap: {
    marginTop: 12,
    position: "relative",
  },
  archiveActionWrap: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: ARCHIVE_ACTION_WIDTH,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 0,
  },
  archiveActionButton: {
    width: 92,
    minHeight: 48,
    borderRadius: 14,
    backgroundColor: "#E11D48",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    shadowColor: "#9F1239",
    shadowOpacity: 0.18,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 3,
  },
  archiveActionPressed: {
    opacity: 0.92,
  },
  archiveActionDisabled: {
    opacity: 0.6,
  },
  archiveActionText: {
    fontSize: 12,
    fontWeight: "800",
    color: "#FFFFFF",
  },

  notifyButton: {
    backgroundColor: "#64748B",
  },
  notifyButtonDisabled: {
    opacity: 0.7,
  },
  notifyButtonText: {
    color: "#fff",
    fontWeight: "800",
    fontSize: 11,
  },
});
